import {ApiResponse, AuthErrorCode} from "./types";
import {ApiClient} from "./apiClient";
import {appConfig} from "../config";

export interface Profile {
    id: string;
    email: string;
    lastLogin: string;
}

export interface LoginRequest {
    username: string;
    password: string;
    stayLoggedIn: boolean;
}

export interface RequestPasswordResetRequest {
    username: string;
}

export interface TokenValidationRequest {
    token: string;
}

export interface ChangePasswordRequest {
    password: string;
    confirmedPassword: string;
    token: string;
}

export interface CompleteAccountSetupRequest {
    password: string;
    confirmedPassword: string;
    token: string;
}

export interface ResendAccountSetupRequest {
    username: string;
}

export interface LoginResult {
    success: boolean;
    errorCode?: AuthErrorCode;
}

export interface PasswordResetResult {
    success: boolean;
    errorCode?: AuthErrorCode;
}

export interface TokenValidationResult {
    success: boolean;
    // data?: TokenValidationData;
    errorCode?: AuthErrorCode;
}

export interface ForgotPasswordResult {
    success: boolean;
    errorCode?: AuthErrorCode;
}

export interface CompleteAccountSetupResult {
    success: boolean;
    errorCode?: AuthErrorCode;
}

const authApiClient = new ApiClient(`${appConfig.backendUrl}/api/auth`);

/**
 * Login with username and password
 * Returns void on success (cookie-based auth)
 */
export const login = (request: LoginRequest): Promise<ApiResponse<void>> => {
    return authApiClient.post('/login', request);
};

/**
 * Request password reset email
 * Returns void on success
 */
export const requestPasswordReset = (request: RequestPasswordResetRequest): Promise<ApiResponse<void>> => {
    return authApiClient.post('/request-password-reset', request);
};

/**
 * Validate password reset token
 * Returns token validation data on success (username, email)
 */
export const validatePasswordResetToken = (request: TokenValidationRequest): Promise<ApiResponse<void>> => {
    return authApiClient.post('/validate-password-reset-token', request);
};

/**
 * Validate account setup token
 * Returns token validation data on success (username, email)
 */
export const validateAccountSetupToken = (request: TokenValidationRequest): Promise<ApiResponse<void>> => {
    return authApiClient.post('/validate-account-setup-token', request);
};

/**
 * Complete account setup with new password
 * Returns void on success
 */
export const completeAccountSetup = (request: CompleteAccountSetupRequest): Promise<ApiResponse<void>> => {
    return authApiClient.post('/complete-account-setup', request);
};

/**
 * Change password using reset token
 * Returns void on success
 */
export const changePassword = (request: ChangePasswordRequest): Promise<ApiResponse<void>> => {
    return authApiClient.post('/change-password', request);
};

/**
 * Resend account setup email
 * Returns void on success
 */
export const resendAccountSetupRequest = (request: ResendAccountSetupRequest): Promise<ApiResponse<void>> => {
    return authApiClient.post('/resend-account-setup-request', request);
};

/**
 * Logout current user
 * Returns void on success
 */
export const logout = (): Promise<ApiResponse<void>> => {
    return authApiClient.post('/logout', {});
};